import { Component, DestroyRef, Inject, OnDestroy, OnInit, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogClose,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { SelectItem } from 'primeng/api';
import { of } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { ReportExportViewService } from '../../../../report-export-view.service';
import { Contact } from '../dynamic-contacts.model';
import { getTokensFromLocalStorage } from '@miq/utils';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { L10nTranslationModule } from 'angular-l10n';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { GetContactInitialsPipe } from '../get-contact-initials.pipe';
import { GetInitialColorPipe } from '../initial-color.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rep-select-contacts',
  standalone: true,
  templateUrl: './select-contacts.component.html',
  styleUrl: './select-contacts.component.scss',
  imports: [
    CommonModule,
    MatButton,
    MatDialogClose,
    MatIcon,
    L10nTranslationModule,
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatCheckbox,
    MatDialogActions,
    GetContactInitialsPipe,
    GetInitialColorPipe,
  ],
})
export class SelectContactsDialogComponent implements OnInit, OnDestroy {
  public searchUser: FormControl;
  public contactList: Contact[] = [];
  public selectedContacts: number[] = [];
  public countriesCatalog: SelectItem[] = [];
  public cbreOnly = true;
  public selectedCountry: string;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public dialogRef: MatDialogRef<SelectContactsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private readonly data,
    private reportExportViewService: ReportExportViewService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.selectedContacts = this.data.selectedContacts;
  }

  ngOnInit() {
    const { userInfo } = getTokensFromLocalStorage();
    this.selectedCountry = JSON.parse(userInfo || '{}').userCountry;
    this.searchUser = this.fb.control('');
    this.initCountriesCatalog();
    this.searchUser.valueChanges
      .pipe(
        debounceTime(400),
        takeUntilDestroyed(this.destroyRef),
        switchMap((searchString: string) =>
          searchString.trim() !== ''
            ? this.reportExportViewService.searchContact(
                searchString.trim(),
                this.selectedCountry ? '&country=' + this.selectedCountry : '',
                this.cbreOnly ? '&cbreOnly=true' : ''
              )
            : of([])
        )
      )
      .subscribe((contacts: Contact[]) => {
        contacts.forEach(contact => {
          contact.checked = this.selectedContacts.includes(contact.id);
        });
        this.contactList = contacts;
        this.cdr.markForCheck();
      });
  }
  private initCountriesCatalog() {
    this.reportExportViewService.getCountryList().subscribe(countriesOptions => {
      // map and prepare all the countries options, appending "Unassigned" option
      this.countriesCatalog = (countriesOptions || []).map(country => ({
        label: country.value,
        value: country.value,
      })) as SelectItem[];
      this.cdr.markForCheck();
    });
  }

  catalogSelection({ value }) {
    this.selectedCountry = value;
    this.searchContacts();
  }

  searchContacts() {
    this.reportExportViewService
      .searchContact(
        this.searchUser.value.trim(),
        this.selectedCountry ? '&country=' + this.selectedCountry : '',
        this.cbreOnly ? '&cbreOnly=true' : ''
      )
      .subscribe((contacts: Contact[]) => {
        contacts.forEach(contact => {
          contact.checked = this.selectedContacts.includes(contact.id);
        });
        this.contactList = contacts;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy() {
    /* required by untilDestroyed and angular-l10n */
  }

  contactChecked(status: boolean, id: number) {
    if (status) {
      this.selectedContacts.push(id);
    } else {
      const index = this.selectedContacts.findIndex(a => a === id);
      this.selectedContacts.splice(index, 1);
    }
  }

  onCreate() {
    this.dialogRef.close(this.selectedContacts);
  }

  onClose() {
    this.dialogRef.close();
  }
}
